import _ from 'lodash'
import {
  ExcelRow,
  ProcessCellForExportParams,
  GridApi,
} from 'ag-grid-community'
import { MutableRefObject, useCallback, useMemo } from 'react'
import { DateTerm } from '../../../../utils/date'
import { intl } from '../../../../i18n'
import { CustomEnumValueDetail } from '../../../../lib/functions/customEnumValue'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import { exportExcel } from '../../../containers/BulkSheet/excel'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import DateVO from '../../../../vo/DateVO'
import {
  ActualWorkingHoursRow,
  convertBreakTime,
  StandardWorkingInfo,
} from '../../ActualWorkingHours/ActualWorkingHours'
import { featureFlagService } from '../../../../utils/feature-flag'

const EXCEL_COLUMN_START_TIME: string = 'D'
const EXCEL_COLUMN_END_TIME: string = 'E'
const EXCEL_COLUMN_BREAK_TIME: string = 'F'
const EXCEL_COLUMN_WORKED_TIME: string = 'G'
const EXCEL_START_INDEX_OFFSET: number = 4 // header row + next

const SECOND_TO_EXCEL_SERIAL_VALUE: number = 3600 * 24

type ExcelHeaderContents = {
  dateTerm: DateTerm
  userName: string | undefined
  standardInfo: StandardWorkingInfo
}

export const useExcelExport = (
  contextRef: MutableRefObject<Record<string, any>>,
  gridApi: GridApi,
  headerContents: ExcelHeaderContents
): (() => void) => {
  const getTotalValueFormula = useCallback(
    (column: string, startRow: number, endRow: number): string => {
      return `=SUM(${column}${startRow}:${column}${endRow})`
    },
    []
  )

  const convertSlashFormat = useCallback((value: string | undefined) => {
    if (!value) return ''
    const date = new DateVO(value)
    return date.format('YYYY/M/D')
  }, [])

  const { fileNamePrefix, prependContent, outputNumOfDays } = useMemo(() => {
    const fileNamePrefix = headerContents.userName
      ? intl.formatMessage(
          {
            id: 'actualWorkingHours.excel.name.prefix.format',
          },
          {
            name: headerContents.userName,
          }
        )
      : intl.formatMessage({
          id: 'actualWorkingHours.excel.title',
        })

    let outputNumOfDays = 0
    if (
      !_.isEmpty(headerContents.dateTerm.startDate) &&
      !_.isEmpty(headerContents.dateTerm.endDate)
    ) {
      const start = dateVoService.construct(headerContents.dateTerm.startDate)
      const end = dateVoService.construct(headerContents.dateTerm.endDate)
      outputNumOfDays = Math.abs(dateVoService.diff(start, end)) + 1
    }
    const prependContent: ExcelRow[] = [
      {
        cells: [],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.excel.user',
              }),
              type: 'String',
            },
            mergeAcross: 1,
          },
          {
            data: {
              value: headerContents.userName || '',
              type: 'String',
            },
          },
        ],
      },
      {
        cells: [
          {
            data: {
              value: '',
              type: 'String',
            },
          },
          {
            data: {
              value: intl.formatMessage({
                id: 'actualWorkingHours.header.term',
              }),
              type: 'String',
            },
            mergeAcross: 1,
          },
          {
            styleId: 'justifyRight',
            data: {
              value: convertSlashFormat(headerContents.dateTerm.startDate),
              type: 'String',
            },
          },
          {
            styleId: 'centerCell',
            data: {
              value: '～',
              type: 'String',
            },
          },
          {
            styleId: 'justifyRight',
            data: {
              value: convertSlashFormat(headerContents.dateTerm.endDate),
              type: 'String',
            },
          },
        ],
      },
      {
        cells: [],
      },
    ]
    return { fileNamePrefix, prependContent, outputNumOfDays }
  }, [convertSlashFormat, getTotalValueFormula, headerContents])

  const workDayTypeNames = useMemo(() => {
    const types: CustomEnumValueDetail[] =
      contextRef.current['workingHoursType']
    if (!types) return []
    const names = types
      .filter(
        t => t.value && ['ATTENDANCE', 'PAID_LEAVE_HALF'].includes(t.value)
      )
      .map(t => getLabel(t.nameI18n) ?? t.name)
    return names
  }, [contextRef])

  const customExportValues = useCallback(
    (params: ProcessCellForExportParams): string | undefined => {
      if (
        !params.node ||
        params.node.rowIndex === null ||
        _.isEmpty(workDayTypeNames)
      ) {
        return undefined
      }
      const rowIndex = featureFlagService.isEnabled(
        'task-actual-work-application'
      )
        ? prependContent.length +
          EXCEL_START_INDEX_OFFSET +
          params.node.rowIndex +
          1
        : prependContent.length +
          EXCEL_START_INDEX_OFFSET +
          params.node.rowIndex -
          1
      const maxRowIndex = featureFlagService.isEnabled(
        'task-actual-work-application'
      )
        ? prependContent.length + EXCEL_START_INDEX_OFFSET + outputNumOfDays
        : prependContent.length + EXCEL_START_INDEX_OFFSET + outputNumOfDays - 2

      const colId: string = params.column.getColId()
      const data = params.node.data
      if (!data) return ''
      if (colId === 'actual.breakTime') {
        if (data.type === 'SUM') {
          return getTotalValueFormula(
            EXCEL_COLUMN_BREAK_TIME,
            rowIndex - 1,
            maxRowIndex
          )
        } else if (params.node?.data) {
          // TODO: If you put a VALUE expression in the total column, the range will be #SPILL and an error will occur
          const row = params.node.data as ActualWorkingHoursRow
          if (!row.actual?.breakTime) return ''
          const breakTime = convertBreakTime(row)
          return (breakTime / SECOND_TO_EXCEL_SERIAL_VALUE).toString()
        }
      } else if (colId === 'actual.workedTime') {
        if (data.type === 'RATIO') {
          return undefined
        }
        if (data.type === 'SUM') {
          return getTotalValueFormula(
            EXCEL_COLUMN_WORKED_TIME,
            rowIndex - 1,
            maxRowIndex
          )
        }
        const startTimeCell = EXCEL_COLUMN_START_TIME + rowIndex
        const endTimeCell = EXCEL_COLUMN_END_TIME + rowIndex
        const breakTimeCell = EXCEL_COLUMN_BREAK_TIME + rowIndex
        const endTime = `IF(VALUE(${endTimeCell}) < VALUE(${startTimeCell}), ${endTimeCell} + "24:00", ${endTimeCell})`
        // If you use the LET function, you will get an error with a spill avoidance "@"
        const formula = `=IF(OR(ISBLANK(${startTimeCell}), ISBLANK(${endTimeCell})), "", 
          IF((${endTime} - ${startTimeCell} - ${breakTimeCell}) > 0, ${endTime} - ${startTimeCell} - ${breakTimeCell}, ""))`
        return formula
      }
      return undefined
    },
    [
      prependContent.length,
      workDayTypeNames,
      outputNumOfDays,
      getTotalValueFormula,
    ]
  )

  const onExportExcel = useCallback(() => {
    const visibleColIds =
      gridApi
        ?.getColumnState()
        .filter(state => !state.hide)
        .map(state => state.colId) || []
    const colIds = (gridApi?.getColumns() ?? [])
      .filter(
        column =>
          column.getColId() === 'blank' ||
          (!['uuid'].includes(column.getColId()) &&
            visibleColIds.includes(column.getColId()))
      )
      .sort((colA, colB) => colA.getInstanceId() - colB.getInstanceId())
    if (!colIds || colIds.length === 0) return

    exportExcel({
      fileNamePrefix,
      gridApi,
      exportColIds: colIds.map(v => v.getColId()),
      prependContent,
      getCustomExportValue: customExportValues,
    })
  }, [gridApi, fileNamePrefix, prependContent, customExportValues])

  return onExportExcel
}
